import { lunchMenuView, viennaToday } from '@pulsify/shared'
import { type ReactNode, useState } from 'react'
import { useVenueRoomStatus } from '~/entities/listing/model/use-venue-room'
import { useListingMenus } from '~/entities/listing-menu/model/use-listing-menus'
import { useOrganizationProfiles } from '~/entities/profile/model/use-profiles'
import { useShareLinks } from '~/entities/share-link/model/use-share-links'
import { CheckinFlyer } from '~/features/events/checkin-flyer/checkin-flyer'
import {
  buildVenueFlyerContent,
  type FlyerContent,
  type FlyerLocale,
  qrShareUrl,
} from '~/features/events/checkin-flyer/flyer-content'
import { PrintFlyer } from '~/features/events/checkin-flyer/print-flyer'
import * as m from '~/paraglide/messages'
import { getListingShareUrl } from '~/shared/lib/share'
import type { Listing } from '~/shared/types'
import { LunchMenuFlyer } from '../components/lunch-menu-flyer'
import {
  buildFollowFlyerContent,
  buildLunchFlyerContent,
  type FlyerTone,
  placeProfileOf,
} from '../lib/place-flyer-content'

export type RenderSheet = (className: string) => ReactNode

interface PlaceFlyerSheet {
  key: 'follow' | 'liveRoom' | 'lunch'
  name: string
  hint: string
  render: RenderSheet
}

export function usePlaceFlyerSheets(
  businessId: string,
  listing: Listing,
  tone: FlyerTone,
  locale: FlyerLocale,
) {
  const [today] = useState(() => viennaToday(new Date()))
  const venue = useVenueRoomStatus(businessId, listing.id)
  const menus = useListingMenus(businessId, listing.id)
  const profiles = useOrganizationProfiles(businessId)
  const placeProfile = placeProfileOf(profiles.profiles, listing.id)
  const shareLinks = useShareLinks(businessId, { kind: 'profile', id: placeProfile?.id ?? '' })
  const shareUrl = qrShareUrl(shareLinks.data) ?? getListingShareUrl(listing.id)
  const shareUrlPending = profiles.isLoading || (placeProfile !== null && shareLinks.isLoading)

  const renderPoster = (content: FlyerContent): RenderSheet =>
    tone === 'white'
      ? (className) => <PrintFlyer content={content} className={className} />
      : (className) => <CheckinFlyer content={content} className={className} />

  const sheets: PlaceFlyerSheet[] = [
    {
      key: 'follow',
      name: m.placeFlyerFollowName(),
      hint: m.placeFlyerFollowHint(),
      render: renderPoster(buildFollowFlyerContent(listing, shareUrl, locale)),
    },
  ]
  if (venue.data) {
    const { code, url } = venue.data
    sheets.push({
      key: 'liveRoom',
      name: m.placeFlyerLiveRoomName(),
      hint: m.placeFlyerLiveRoomHint(),
      render: renderPoster(buildVenueFlyerContent(listing, { code, url }, 'onsite', locale)),
    })
  }
  const lunch = menus.data?.lunch
  if (lunch && lunchMenuView(lunch, today)) {
    const content = buildLunchFlyerContent(listing, lunch, shareUrl, locale)
    sheets.push({
      key: 'lunch',
      name: m.placeFlyerLunchName(),
      hint: m.placeFlyerLunchHint(),
      render: (className) => <LunchMenuFlyer content={content} tone={tone} className={className} />,
    })
  }

  return {
    sheets: shareUrlPending ? [] : sheets,
    shareUrl,
    loading: venue.isLoading || menus.isLoading || shareUrlPending,
  }
}
